/**
 * Teléfono de la dirección de entrega: la misma regla que la tienda (Magento,
 * `Puntoseco\CustomerAddress\Helper\Phone` y el widget del teléfono), para que un pedido
 * no salga con un número distinto según quién lo cargó.
 *
 * Un teléfono sirve sólo si queda en el número nacional argentino de 10 dígitos —código
 * de área + abonado, sin 54, sin 9, sin 0 y sin 15— con un código de área real (empieza
 * con 1, 2 o 3). El caso que lo originó: pedidos de CMP que llegaron al ERP con
 * `011 3144-05`, dos dígitos menos, y nada los frenó.
 *
 * Sólo valida: lo que se guarda sigue siendo lo que emite `PhoneNumberInput`
 * (`541131440512`), que la integración de pedidos ya lleva al nacional.
 */
const NATIONAL_LENGTH = 10;

/** El nacional de 10 dígitos si se reconoce; si no, los dígitos que haya. */
export const normalizeArPhone = (raw?: string | null): string => {
  let digits = String(raw ?? "").replace(/\D+/g, "");
  if (!digits) return "";
  const onlyDigits = digits;

  // Prefijo internacional a la vieja usanza (0054…).
  if (digits.startsWith("00") && digits.length > 2) digits = digits.slice(2);
  // País. Sólo si sobra número atrás: un abonado puede empezar con 54.
  if (digits.length > NATIONAL_LENGTH && digits.startsWith("54")) digits = digits.slice(2);
  // El 9 del celular en formato internacional es el mismo dato que el 15 local.
  if (digits.length === NATIONAL_LENGTH + 1 && digits.startsWith("9")) digits = digits.slice(1);
  // 0 de larga distancia.
  if (digits.length > NATIONAL_LENGTH && digits.startsWith("0")) digits = digits.slice(1);
  // 15 de celular, entre el área (2, 3 o 4 dígitos) y el abonado.
  if (digits.length === NATIONAL_LENGTH + 2) {
    for (const areaLength of [2, 3, 4]) {
      if (digits.substr(areaLength, 2) === "15") {
        digits = digits.slice(0, areaLength) + digits.slice(areaLength + 2);
        break;
      }
    }
  }

  // Los códigos de área argentinos empiezan con 1, 2 o 3: "+54 35173309" no es un
  // nacional de 10, es un 54 pegado a un número incompleto.
  if (digits.length !== NATIONAL_LENGTH || !/^[123]/.test(digits)) return onlyDigits;
  return digits;
};

export const isValidArPhone = (raw?: string | null): boolean =>
  /^[123][0-9]{9}$/.test(normalizeArPhone(raw));

export const AR_PHONE_INVALID_MESSAGE =
  "Ingresá el código de área y el número, 10 dígitos. Ej: 1123456789";
